import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import '../styles/ArchivedModal.css';

function ArchivedClientsModal({ onClose, onRestore }) {
  const [archivedClients, setArchivedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load archived clients when modal opens
  useEffect(() => {
    loadArchivedClients();
  }, []);

  const loadArchivedClients = async () => {
    setLoading(true);
    setError('');

    try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients/archived`, {
            withCredentials: true  // Include session cookies!
        });
      setArchivedClients(response.data.clients);
    } catch (err) {
      setError('Failed to load archived clients');
      console.error('Error loading archived clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (client) => {
    if (!window.confirm(`Restore "${client.name}"?`)) {
      return;
    }

    try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/clients/${client.id}/restore`, {}, {
            withCredentials: true  // Include session cookies!
        });

      if (response.data.success) {
        toast.success(`♻️ ${client.name} restored!`);
        
        // Remove from archived list
        setArchivedClients(prev => prev.filter(c => c.id !== client.id));
        
        // Notify parent to refresh main client list
        onRestore();
      }
    } catch (err) {
      toast.error('Failed to restore client');
      console.error('Error restoring client:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="archived-modal-overlay" onClick={onClose}>
      <div className="archived-modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="archived-modal-header">
          <h2>📦 Archived Clients</h2>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        {/* Content */}
        <div className="archived-modal-body">
          
          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading archived clients...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-state">
              <p>{error}</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && archivedClients.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No Archived Clients</h3>
              <p>All clients are active!</p>
            </div>
          )}

          {/* Archived Clients List */}
          {!loading && !error && archivedClients.length > 0 && (
            <div className="archived-list">
              {archivedClients.map(client => (
                <div key={client.id} className="archived-client-item">
                  
                  <div className="archived-client-info">
                    <h3>{client.name}</h3>
                    
                    <div className="archived-meta">
                      <span className="archive-date">
                        🗓️ Archived: {formatDate(client.deleted_at)}
                      </span>
                      
                      {client.analytics_count > 0 && (
                        <span className="data-badge">
                          📊 {client.analytics_count} analytics records
                        </span>
                      )}
                      
                      {client.posts_count > 0 && (
                        <span className="data-badge">
                          📝 {client.posts_count} posts
                        </span>
                      )}
                    </div>

                    <div className="platforms-archived">
                      {client.instagram_id && (
                        <span className="platform-badge instagram">📷 Instagram</span>
                      )}
                      {client.facebook_id && (
                        <span className="platform-badge facebook">📘 Facebook</span>
                      )}
                    </div>
                  </div>

                  <div className="archived-actions">
                    <button 
                      onClick={() => handleRestore(client)}
                      className="btn-restore"
                    >
                      ♻️ Restore
                    </button>
                  </div>
                  
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="archived-modal-footer">
          <p className="archive-note">
            💡 Archived clients preserve all historical data (analytics, posts, reports)
          </p>
        </div>

      </div>
    </div>
  );
}

export default ArchivedClientsModal;